import React from 'react';
import {
  Box,
  Button,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';

import { buildDepthAnalyticsView, listDepthCages, listDepthDays } from './depthAnalytics';

const ALL_DAYS = '__all__';
const CENTER_SCOPE = '__center__';
// A la derecha queda una sola magnitud, el % del tiempo alimentado. Las alarmas y la variable del
// segundo carril se leen por color en los carriles, y su número vive en el tooltip de cada banda y
// en la tabla «Datos»: la columna dejó de ser un muro de cifras que nadie comparaba.
//
// Las barras de tiempo son elásticas y en un diálogo ancho se comían la pantalla mientras los
// carriles quedaban en dos rendijas. Se acota el bloque del eje y se ensanchan los carriles: el
// color necesita superficie para leerse, la barra ya se entiende con menos.
const GRID = '48px 1fr 96px 46px 46px 1fr 48px';
const AXIS_MAX_W = 1020;
const LOW_COVERAGE_PCT = 25;
const BAND_H = 22;

// Cuatro pasos por cuartos del máximo de la columna. El cero tiene clase propia: no es «poco», es
// medido y vale cero. La ausencia de dato no es ninguna de las cinco.
const RAMP_ALARMS = ['rgba(255,82,82,.18)', 'rgba(255,82,82,.40)', 'rgba(255,82,82,.66)', 'rgba(255,82,82,.95)'];
const RAMP_LANE = ['rgba(127,147,163,.18)', 'rgba(127,147,163,.40)', 'rgba(127,147,163,.66)', 'rgba(127,147,163,.95)'];
const ZERO_FILL = 'rgba(255,255,255,0.045)';
// La celda no llena su cuadro: se separa de la vecina y redondea. Sin ese aire, dos bandas
// contiguas del mismo cuarto se fundían en un bloque y se perdía el conteo de bandas.
const LANE_CELL_SX = { height: '100%', px: '2px', pb: '2px', boxSizing: 'border-box', display: 'flex' };
const LANE_INNER_SX = { flex: 1, borderRadius: '3px' };
const NO_DATA_FILL = 'repeating-linear-gradient(45deg,rgba(255,255,255,.05) 0 3px,transparent 3px 7px)';

/** El tramado marca que el conteo subestima: la banda tuvo poca exposición, no poca pérdida. */
const lowFill = (rgb) => `repeating-linear-gradient(45deg,${rgb} 0 2px,transparent 2px 6px)`;

const laneFill = (value, max, ramp, lowSample) => {
  if (value === null || value === undefined) return { background: NO_DATA_FILL };
  if (!value) return { bgcolor: ZERO_FILL, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.06)' };
  const quarter = max > 0 ? Math.min(3, Math.floor((value / max) * 4 - 1e-9)) : 0;
  const colour = ramp[Math.max(0, quarter)];
  return lowSample ? { background: lowFill(colour) } : { bgcolor: colour };
};

const C = {
  txt: 'rgba(255,255,255,0.88)',
  txt2: 'rgba(255,255,255,0.60)',
  txt3: 'rgba(255,255,255,0.40)',
  txt4: 'rgba(255,255,255,0.24)',
  border: 'rgba(255,255,255,0.09)',
  borderStrong: 'rgba(255,255,255,0.15)',
  accent: '#e84e0e',
  accentSoft: 'rgba(232,78,14,0.13)',
  fed: '#43c47a',
  unfed: '#7f93a3',
  err: '#ff5252',
  warn: '#ffb300',
};

/*
 * Escala tipográfica de la vista:
 *   18    respuesta destacada
 *   13    prosa
 *   12.5  título de sección
 *   11.5  dato en fila de tabla o de tooltip
 *   10.5  título de grupo, encabezado de columna y nota al pie
 */
const SECTION_SX = { fontSize: 12.5, fontWeight: 700, color: C.txt2 };
const GROUP_SX = {
  fontSize: 10.5,
  textTransform: 'uppercase',
  letterSpacing: '.05em',
  fontWeight: 800,
  color: C.txt3,
};

const decimals = (value, count = 1) =>
  Number.isFinite(Number(value)) ? Number(value).toFixed(count) : '—';
const metres = (value) =>
  Number.isFinite(Number(value)) ? Number(Number(value).toFixed(2)).toString() : '—';
const counter = (value) => (Number.isFinite(Number(value)) ? Number(value).toLocaleString() : '—');

/**
 * Variables del segundo carril. Ambas son medidas por el backend en `depth_strata`, sobre los
 * mismos intervalos y las mismas bandas que el carril de alarmas: no hay que advertir de
 * resoluciones distintas porque no las hay.
 */
const LANES = [
  {
    id: 'perdidos',
    field: 'perdidos',
    labelKey: 'analytics_depth_perdidos',
    chipKey: 'analytics_depth_perdidos_long',
    shortKey: 'analytics_depth_perdidos_short',
    dec: 0,
  },
  {
    id: 'alimentoKg',
    field: 'alimentoKg',
    labelKey: 'analytics_depth_food_kg',
    chipKey: 'analytics_depth_food_kg',
    shortKey: 'analytics_depth_food_kg_short',
    dec: 1,
  },
];

/**
 * Rótulo de carril en diagonal. Una columna de 46 px no admite «Alarmas» en horizontal, y
 * ensancharla para que quepa sería gastar en el texto el espacio que necesita el color.
 *
 * El texto nace en el centro de la columna, no en su borde. Una caja estirada pondría el eje de
 * giro en el borde izquierdo, y entonces cualquier sangrado desplaza el rótulo *sobre la
 * diagonal* —lo sube tanto como lo corre—. Con ancho cero centrado en la pista, el punto de giro
 * es el medio de la columna y el rótulo sale de ahí.
 */
const LANE_HEAD_SX = {
  ...GROUP_SX,
  transform: 'rotate(-45deg)',
  transformOrigin: 'left bottom',
  whiteSpace: 'nowrap',
  alignSelf: 'end',
  justifySelf: 'center',
  width: 0,
};

/** `FeederScore_depth_ladder_m` viaja como lista o como texto según el origen de la configuración. */
const parseLadder = (raw) => {
  if (Array.isArray(raw)) return raw.map(Number).filter(Number.isFinite);
  if (typeof raw === 'number') return [raw];
  if (typeof raw !== 'string') return [];
  return raw.replace(/[[\]]/g, '').split(',').map(Number).filter(Number.isFinite);
};

// El tema define `MuiSelect` con `fullWidth: true`, así que un selector suelto ocuparía toda la
// fila. Se acota el ancho y se deja el resto del estilo —borde, alto, dropdown oscuro— al tema.
const selectSx = { bgcolor: '#070d18' };

const bannerSx = (tone) => ({
  mt: 2,
  px: 1.5,
  py: 1.1,
  fontSize: 12,
  lineHeight: 1.5,
  borderRadius: 1,
  bgcolor: tone === 'warn' ? 'rgba(255,179,0,0.12)' : 'rgba(127,147,163,0.10)',
  border: `1px solid ${tone === 'warn' ? 'rgba(255,179,0,0.28)' : 'rgba(127,147,163,0.26)'}`,
  color: tone === 'warn' ? '#ffe0a3' : C.txt2,
});

// `stickyHeader` pinta la cabecera con `background.default`, que en modo claro es blanco.
const headCellSx = { ...GROUP_SX, bgcolor: '#0a121f', borderColor: C.border };
const bodyCellSx = {
  color: C.txt2,
  fontSize: 11.5,
  borderColor: C.border,
  fontVariantNumeric: 'tabular-nums',
};

/**
 * Tooltip de banda, con el trato del gráfico de detalle diario: título centrado, una fila por
 * magnitud, etiqueta en el color de su serie y valor alineado a la derecha. En una sola línea las
 * cinco cifras se leían como una frase y había que contar separadores para saber cuál era cuál.
 */
const BandTooltip = ({ title, rows, note }) => (
  <Box sx={{ py: 0.3, minWidth: 190 }}>
    <Typography sx={{
      fontSize: 12.5,
      fontWeight: 800,
      textAlign: 'center',
      color: C.txt,
      pb: 0.6,
      mb: 0.6,
      borderBottom: `1px solid ${C.borderStrong}`,
    }}
    >
      {title}
    </Typography>
    {rows.map((row) => (
      <Box
        key={row.label}
        sx={{
          display: 'grid',
          gridTemplateColumns: 'max-content auto',
          columnGap: 1.4,
          alignItems: 'baseline',
          py: 0.18,
        }}
      >
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: row.colour || C.txt2, whiteSpace: 'nowrap' }}>
          {`${row.label}:`}
        </Typography>
        <Typography sx={{
          fontSize: 11.5,
          textAlign: 'right',
          color: row.value === '—' ? C.txt4 : C.txt,
          fontVariantNumeric: 'tabular-nums',
          whiteSpace: 'nowrap',
        }}
        >
          {row.value}
        </Typography>
      </Box>
    ))}
    {note && (
      <Typography sx={{
        fontSize: 10.5,
        color: C.warn,
        mt: 0.7,
        pt: 0.6,
        borderTop: `1px solid ${C.border}`,
      }}
      >
        {note}
      </Typography>
    )}
  </Box>
);

const TOOLTIP_SLOT = {
  tooltip: {
    sx: {
      bgcolor: 'rgba(0,0,0,0.96)',
      border: `1px solid ${C.accent}`,
      borderRadius: 1,
      px: 1.2,
      py: 0.7,
      maxWidth: 'none',
    },
  },
};

const Row = ({ label, value, muted = false, valueColor }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1, py: 0.3 }}>
    <Typography sx={{ fontSize: 12, color: muted ? C.txt3 : C.txt2 }}>{label}</Typography>
    <Typography sx={{
      fontSize: 12,
      fontWeight: muted ? 600 : 700,
      color: valueColor || (muted ? C.txt2 : C.txt),
      fontVariantNumeric: 'tabular-nums',
    }}
    >
      {value}
    </Typography>
  </Box>
);

// Con el diálogo ancho, un bloque elástico separaría tanto la etiqueta de su valor que dejarían de
// leerse como par. Se acota el bloque, no el diálogo.
const Block = ({ title, children }) => (
  <Box sx={{ flex: '0 1 320px', maxWidth: 340 }}>
    <Typography sx={{ ...GROUP_SX, mb: 1 }}>{title}</Typography>
    {children}
  </Box>
);

const DepthAnalyticsDialog = ({
  open,
  onClose,
  jaulas = [],
  preferredCage = '',
  defaultBinWidth = 1,
  defaultMinValid = 0.5,
  depthLadder = [],
  translate = (key) => key,
}) => {
  const ladder = React.useMemo(() => parseLadder(depthLadder), [depthLadder]);
  const availableCages = React.useMemo(() => listDepthCages(jaulas), [jaulas]);
  const [scope, setScope] = React.useState('');
  const [period, setPeriod] = React.useState(ALL_DAYS);
  const [filterEnabled, setFilterEnabled] = React.useState(true);
  const [threshold, setThreshold] = React.useState(Number(defaultMinValid) || 0.5);
  const [tableOpen, setTableOpen] = React.useState(false);
  const [laneId, setLaneId] = React.useState(LANES[0].id);

  React.useEffect(() => {
    if (!open || !availableCages.length) return;
    const known = scope === CENTER_SCOPE || availableCages.some((cage) => cage?.name === scope);
    if (known) return;
    const preferred = availableCages.find((cage) => cage?.name === preferredCage);
    setScope((preferred || availableCages[0]).name);
  }, [availableCages, open, preferredCage, scope]);

  const isCenter = scope === CENTER_SCOPE;
  const scopedCages = React.useMemo(
    () => (isCenter
      ? availableCages
      : availableCages.filter((cage) => cage?.name === scope)),
    [availableCages, isCenter, scope],
  );

  const days = React.useMemo(() => listDepthDays(scopedCages), [scopedCages]);
  React.useEffect(() => {
    if (period !== ALL_DAYS && !days.includes(period)) setPeriod(ALL_DAYS);
  }, [days, period]);
  const selectedDays = React.useMemo(
    () => (period === ALL_DAYS ? days : [period]),
    [days, period],
  );

  const preview = React.useMemo(
    () => buildDepthAnalyticsView({
      jaulas: scopedCages,
      selectedDays,
      threshold: defaultMinValid,
      defaultBinWidth,
      defaultMinValid,
    }),
    [defaultBinWidth, defaultMinValid, scopedCages, selectedDays],
  );
  const thresholdOptions = React.useMemo(() => {
    const values = new Set([preview.minValid]);
    preview.rows.forEach((row) => {
      if (Number.isFinite(row.from) && row.from >= preview.minValid) values.add(row.from);
      if (Number.isFinite(row.to) && row.to >= preview.minValid) values.add(row.to);
    });
    return Array.from(values).sort((left, right) => left - right);
  }, [preview]);
  React.useEffect(() => {
    if (!thresholdOptions.some((value) => Math.abs(value - threshold) < 1e-9)) {
      setThreshold(thresholdOptions[0] ?? Number(defaultMinValid) ?? 0.5);
    }
  }, [defaultMinValid, threshold, thresholdOptions]);

  const view = React.useMemo(
    () => buildDepthAnalyticsView({
      jaulas: scopedCages,
      selectedDays,
      filterEnabled,
      threshold,
      defaultBinWidth,
      defaultMinValid,
      ladder,
    }),
    [defaultBinWidth, defaultMinValid, filterEnabled, ladder, scopedCages, selectedDays, threshold],
  );

  const hourUnit = translate('analytics_depth_hour_unit');
  const minuteUnit = translate('analytics_depth_minute_unit');
  const formatDuration = React.useCallback((minutes) => {
    const total = Math.max(0, Math.round(Number(minutes) || 0));
    if (total < 60) return `${total} ${minuteUnit}`;
    const hours = Math.floor(total / 60);
    const rest = total % 60;
    if (hours >= 24 || rest === 0) return `${hours.toLocaleString()} ${hourUnit}`;
    return `${hours} ${hourUnit} ${rest} ${minuteUnit}`;
  }, [hourUnit, minuteUnit]);

  const coveragePercent = view.coveragePct;
  const lowCoverage = coveragePercent !== null && coveragePercent < LOW_COVERAGE_PCT;
  const coverageColor = coveragePercent === null
    ? C.txt3
    : (coveragePercent >= 85 ? C.fed : (lowCoverage ? C.err : C.warn));
  const maxPercent = Math.max(
    ...view.rows.map((row) => Math.max(row.fedPct || 0, row.unfedPct || 0)),
    1,
  );
  const lane = LANES.find((entry) => entry.id === laneId) || LANES[0];
  const laneAvailable = view.strataCoverage.present;
  const laneLabel = translate(lane.labelKey);
  const laneValue = (row) => (laneAvailable ? row[lane.field] : null);
  const laneText = (row) => {
    const value = laneValue(row);
    return value === null || value === undefined ? '—' : decimals(value, lane.dec);
  };
  const thresholdIndex = thresholdOptions.findIndex(
    (value) => Math.abs(value - threshold) < 1e-9,
  );
  const step = (delta) => {
    const next = thresholdOptions[Math.min(
      thresholdOptions.length - 1,
      Math.max(0, thresholdIndex + delta),
    )];
    if (next !== undefined) {
      setFilterEnabled(true);
      setThreshold(next);
    }
  };
  const ladderLine = view.ladderStops.length
    ? view.ladderStops.map((stop) => [
      `${metres(stop.depthM)} m`,
      translate(stop.role === 'initial'
        ? 'analytics_depth_ladder_initial'
        : 'analytics_depth_ladder_after_alarm'),
      stop.alarms === null
        ? '—'
        : `${counter(stop.alarms)} ${translate('analytics_depth_alarm_count')}`,
    ].join(' · ')).join('     ')
    : '';
  const reasonText = (reason) => {
    if (reason.code === 'coverage') {
      return translate('analytics_depth_reason_coverage', { percent: decimals(reason.percent, 0) });
    }
    if (reason.code === 'no_standout') {
      return translate('analytics_depth_reason_no_standout', { factor: decimals(reason.factor) });
    }
    return translate('analytics_depth_reason_no_sample');
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      // Centrado vertical, el borde superior de un eje largo queda bajo la barra de la app (7vh).
      // Se ancla arriba y se acota el alto: el contenido desplaza dentro del diálogo, y la cabecera
      // con los selectores y el pie con el cierre quedan siempre a la vista. Los márgenes dejan el
      // diálogo libre de la barra incluso centrado, porque el margen entra en el centrado flex:
      // 8 + 82 + 4 = 94vh de caja, o sea 3vh arriba y el borde superior en 11vh.
      sx={{ '& .MuiDialog-container': { alignItems: 'flex-start' } }}
      PaperProps={{
        sx: {
          width: 1240,
          maxWidth: '96vw',
          m: 0,
          mt: '8vh',
          mb: '4vh',
          maxHeight: '82vh',
          bgcolor: '#0a121f',
          color: C.txt,
          backgroundImage: 'none',
        },
      }}
    >
      <DialogTitle sx={{ py: 1.4, px: 2.5, borderBottom: `1px solid ${C.border}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: 14.5, fontWeight: 800, mr: 0.5 }}>
            {translate('analytics_depth_open')}
          </Typography>
          {!!availableCages.length && (
            <>
              <Select
                size="small"
                fullWidth={false}
                value={scope || ''}
                onChange={(event) => setScope(event.target.value)}
                sx={{ ...selectSx, width: 132 }}
                aria-label={translate('analytics_depth_scope')}
              >
                <MenuItem value={CENTER_SCOPE}>{translate('analytics_general')}</MenuItem>
                {availableCages.map((cage) => (
                  <MenuItem key={cage.name} value={cage.name}>{cage.name}</MenuItem>
                ))}
              </Select>
              <Select
                size="small"
                fullWidth={false}
                value={period}
                onChange={(event) => setPeriod(event.target.value)}
                sx={{ ...selectSx, width: 158 }}
                aria-label={translate('analytics_depth_period')}
              >
                <MenuItem value={ALL_DAYS}>{translate('analytics_all')}</MenuItem>
                {days.map((day) => <MenuItem key={day} value={day}>{day}</MenuItem>)}
              </Select>
            </>
          )}
          <Box sx={{ flex: 1 }} />
          {coveragePercent !== null && (
            <Tooltip title={translate('analytics_depth_coverage')}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.9 }}>
                <Box sx={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  display: 'grid',
                  placeItems: 'center',
                  background: `conic-gradient(${coverageColor} 0 ${coveragePercent.toFixed(1)}%, rgba(255,255,255,.09) ${coveragePercent.toFixed(1)}% 100%)`,
                }}
                >
                  <Box sx={{ width: 17, height: 17, borderRadius: '50%', bgcolor: '#0a121f' }} />
                </Box>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: coverageColor, fontVariantNumeric: 'tabular-nums' }}>
                  {`${coveragePercent.toFixed(0)}%`}
                </Typography>
                <Typography sx={{ fontSize: 11, color: lowCoverage ? C.warn : C.txt3 }}>
                  {translate(lowCoverage ? 'analytics_depth_low_coverage' : 'analytics_depth_with_data')}
                </Typography>
              </Box>
            </Tooltip>
          )}
          <IconButton size="small" onClick={onClose} sx={{ color: C.txt3 }} aria-label={translate('analytics_close')}>
            <CloseRoundedIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pt: 0, pb: 1 }}>
        {!availableCages.length ? (
          <Box sx={{ textAlign: 'center', py: 5 }}>
            <Typography sx={{ fontSize: 24, color: C.txt3 }}>⌀</Typography>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, mt: 1 }}>
              {translate('analytics_depth_no_measurements')}
            </Typography>
          </Box>
        ) : (
          <>
            {view.contractMismatch && (
              <Box sx={bannerSx('warn')}>{translate('analytics_depth_contract_mismatch')}</Box>
            )}
            {!view.hasWindowData && (
              <Box sx={bannerSx('info')}>{translate('analytics_depth_historical_alarm_only')}</Box>
            )}

            {/* ---------- respuesta o aviso de confianza ---------- */}
            {view.confidence.publishable ? (
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.4, mt: 1.8 }}>
                <Typography sx={{
                  fontSize: 18,
                  fontWeight: 800,
                  lineHeight: 1.2,
                  color: C.err,
                  fontVariantNumeric: 'tabular-nums',
                  flex: 'none',
                }}
                >
                  {`${view.highlight.label} m`}
                </Typography>
                <Box>
                  <Typography sx={{ fontSize: 13, color: C.txt, lineHeight: 1.45 }}>
                    {translate('analytics_depth_headline', {
                      alarms: counter(view.highlight.alarms),
                      time: formatDuration(view.highlight.fed),
                      factor: decimals(view.highlightFactor),
                      percent: decimals(view.highlight.fedPct, 0),
                    })}
                  </Typography>
                  {!!ladderLine && (
                    <Typography sx={{ fontSize: 11.5, color: C.txt3, mt: 0.4 }}>
                      {`${translate('analytics_depth_expected_ladder')} — ${ladderLine}`}
                    </Typography>
                  )}
                </Box>
              </Box>
            ) : (
              <Box sx={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 1.4,
                mt: 1.8,
                px: 1.6,
                py: 1.1,
                borderLeft: `3px solid ${C.warn}`,
                borderRadius: '0 7px 7px 0',
                bgcolor: 'rgba(255,179,0,0.08)',
              }}
              >
                <Typography sx={{ fontSize: 15, fontWeight: 800, color: C.warn, lineHeight: 1.2 }}>!</Typography>
                <Box>
                  <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#ffe0a3' }}>
                    {translate('analytics_depth_no_conclusion')}
                  </Typography>
                  <Typography sx={{ fontSize: 11.5, color: C.txt3, mt: 0.4, lineHeight: 1.5 }}>
                    {translate('analytics_depth_no_conclusion_why', {
                      reasons: view.confidence.reasons.map(reasonText).join(' · '),
                    })}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* ---------- eje de profundidad ---------- */}
            <Box sx={{ mt: 2, maxWidth: AXIS_MAX_W, mx: 'auto' }}>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                gap: 1.1,
                pb: 1,
              }}
              >
                <Typography sx={{ fontSize: 10.5, color: C.txt3 }}>
                  {translate('analytics_depth_second_lane')}
                </Typography>
                <Box sx={{ display: 'flex', border: `1px solid ${C.border}`, borderRadius: 1, overflow: 'hidden' }}>
                  {LANES.map((entry) => (
                    <Button
                      key={entry.id}
                      onClick={() => setLaneId(entry.id)}
                      disabled={!laneAvailable}
                      sx={{
                        minWidth: 0,
                        px: 1.1,
                        py: 0.2,
                        fontSize: 10.5,
                        borderRadius: 0,
                        textTransform: 'none',
                        color: entry.id === laneId ? C.txt : C.txt3,
                        bgcolor: entry.id === laneId ? 'rgba(255,255,255,0.07)' : '#070d18',
                        '&.Mui-disabled': { color: C.txt4 },
                      }}
                    >
                      {translate(entry.chipKey)}
                    </Button>
                  ))}
                </Box>
                <Box sx={{ flex: 1 }} />
                <Typography sx={{ fontSize: 10.5, color: C.txt3 }}>
                  {translate('analytics_depth_min_visible')}
                </Typography>
                <Box sx={{
                  display: 'flex',
                  alignItems: 'center',
                  border: `1px solid ${C.border}`,
                  borderRadius: 1,
                  bgcolor: '#070d18',
                }}
                >
                  <IconButton
                    size="small"
                    sx={{ p: 0.1, color: C.txt3, borderRadius: 0 }}
                    onClick={() => step(-1)}
                    disabled={thresholdIndex <= 0}
                    aria-label={translate('analytics_depth_min_visible')}
                  >
                    <KeyboardArrowDownRoundedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                  <Typography sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    px: 0.9,
                    color: filterEnabled ? C.warn : C.txt3,
                    borderLeft: `1px solid ${C.border}`,
                    borderRight: `1px solid ${C.border}`,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                  >
                    {filterEnabled ? `${metres(view.effectiveThreshold)} m` : '—'}
                  </Typography>
                  <IconButton
                    size="small"
                    sx={{ p: 0.1, color: C.txt3, borderRadius: 0 }}
                    onClick={() => step(1)}
                    disabled={thresholdIndex >= thresholdOptions.length - 1}
                    aria-label={translate('analytics_depth_min_visible')}
                  >
                    <KeyboardArrowUpRoundedIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
                <Button
                  size="small"
                  onClick={() => setFilterEnabled((previous) => !previous)}
                  sx={{
                    fontSize: 10,
                    color: C.txt3,
                    textDecoration: 'underline',
                    minWidth: 0,
                    p: 0,
                    textTransform: 'none',
                  }}
                >
                  {translate(filterEnabled
                    ? 'analytics_depth_filter_remove'
                    : 'analytics_depth_filter_apply')}
                </Button>
              </Box>

              <Box sx={{
                display: 'grid',
                gridTemplateColumns: GRID,
                alignItems: 'end',
                minHeight: 40,
                pb: 1.1,
              }}
              >
                <Typography sx={{ ...GROUP_SX, gridColumn: '1 / 3', color: C.unfed }}>
                  {`◄ ${translate('analytics_depth_pct_of_unfed_time')}`}
                </Typography>
                <Typography sx={{ ...LANE_HEAD_SX, gridColumn: '4', color: C.err }}>
                  {translate('analytics_depth_alarms_short')}
                </Typography>
                <Typography sx={{ ...LANE_HEAD_SX, gridColumn: '5', color: C.txt2 }}>
                  {translate(lane.shortKey)}
                </Typography>
                <Typography sx={{ ...GROUP_SX, gridColumn: '6 / 8', textAlign: 'right', color: C.fed }}>
                  {`${translate('analytics_depth_pct_of_fed_time')} ►`}
                </Typography>
              </Box>

              {view.axis.map((entry) => {
                // Un corte: más metros vacíos de los que vale la pena dibujar. Ocupa el tope y
                // rotula los metros reales, para que el salto no se lea como una banda más.
                if (entry.kind === 'gap') {
                  return (
                    <Box
                      key={`gap-${entry.from}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: GRID,
                        alignItems: 'center',
                        height: entry.cappedBands * BAND_H,
                      }}
                    >
                      <Box sx={{
                        gridColumn: '3',
                        height: '100%',
                        borderLeft: '1px solid rgba(255,255,255,.06)',
                        borderRight: '1px solid rgba(255,255,255,.06)',
                        background: NO_DATA_FILL,
                      }}
                      />
                      <Typography sx={{
                        gridColumn: '4 / 8',
                        pl: 1.3,
                        fontSize: 10.5,
                        color: C.txt4,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                      }}
                      >
                        {translate('analytics_depth_gap', { metres: metres(entry.metres) })}
                      </Typography>
                    </Box>
                  );
                }
                // Banda visitada sin nada que contar: ocupa su metro, como cualquier otra.
                if (entry.kind === 'void') {
                  return (
                    <Box
                      key={`void-${entry.from}`}
                      sx={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', height: BAND_H }}
                    >
                      <Typography sx={{
                        gridColumn: '3',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 11.5,
                        color: C.txt4,
                        fontVariantNumeric: 'tabular-nums',
                        borderLeft: '1px solid rgba(255,255,255,.06)',
                        borderRight: '1px solid rgba(255,255,255,.06)',
                      }}
                      >
                        {entry.label}
                      </Typography>
                      <Box sx={{ ...LANE_CELL_SX, gridColumn: '4' }}>
                        <Box sx={{ ...LANE_INNER_SX, background: NO_DATA_FILL }} />
                      </Box>
                      <Box sx={{ ...LANE_CELL_SX, gridColumn: '5' }}>
                        <Box sx={{ ...LANE_INNER_SX, background: NO_DATA_FILL }} />
                      </Box>
                    </Box>
                  );
                }

                const row = entry.row;
                const isLadder = view.ladderStops.some((stop) => stop.label === row.label);
                const secondary = laneValue(row);
                return (
                  <Tooltip
                    key={row.label}
                    slotProps={TOOLTIP_SLOT}
                    title={(
                      <BandTooltip
                        title={translate('analytics_depth_band_title', { band: row.label })}
                        note={row.lowSample ? translate('analytics_depth_low_sample') : ''}
                        rows={[
                          {
                            label: translate('analytics_depth_fed'),
                            colour: C.fed,
                            value: row.fed === null
                              ? '—'
                              : `${formatDuration(row.fed)} · ${decimals(row.fedPct, 0)}%`,
                          },
                          {
                            label: translate('analytics_depth_unfed'),
                            colour: C.unfed,
                            value: row.unfed === null
                              ? '—'
                              : `${formatDuration(row.unfed)} · ${decimals(row.unfedPct, 0)}%`,
                          },
                          { label: translate('analytics_depth_alarm_count'), colour: C.err, value: counter(row.alarms) },
                          // Las dos magnitudes medidas van siempre, no sólo la del carril
                          // elegido: el tooltip es donde ahora viven los números.
                          {
                            label: translate('analytics_depth_perdidos'),
                            value: row.perdidos === null ? '—' : counter(row.perdidos),
                          },
                          {
                            label: translate('analytics_depth_food_kg'),
                            value: row.alimentoKg === null ? '—' : decimals(row.alimentoKg),
                          },
                        ]}
                      />
                    )}
                  >
                    <Box
                      aria-label={`${row.label} m · ${decimals(row.fedPct)}% ${translate('analytics_depth_fed')} · ${decimals(row.unfedPct)}% ${translate('analytics_depth_unfed')} · ${counter(row.alarms)} ${translate('analytics_depth_alarm_count')} · ${laneText(row)} ${laneLabel}`}
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: GRID,
                        alignItems: 'center',
                        height: BAND_H,
                        position: 'relative',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' },
                      }}
                    >
                      <Typography sx={{ gridColumn: '1', fontSize: 11.5, color: C.txt3, fontVariantNumeric: 'tabular-nums' }}>
                        {row.unfedPct !== null && row.unfedPct >= 0.5 ? `${decimals(row.unfedPct, 0)}%` : ''}
                      </Typography>
                      <Box sx={{ gridColumn: '2', display: 'flex', justifyContent: 'flex-end', pr: 1.3, minWidth: 0 }}>
                        <Box sx={{
                          height: 10,
                          borderRadius: '2px',
                          minWidth: '2px',
                          bgcolor: C.unfed,
                          opacity: 0.6,
                          width: `${((row.unfedPct || 0) / maxPercent) * 100}%`,
                        }}
                        />
                      </Box>
                      <Box sx={{
                        gridColumn: '3',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderLeft: '1px solid rgba(255,255,255,.06)',
                        borderRight: '1px solid rgba(255,255,255,.06)',
                      }}
                      >
                        {isLadder && (
                          <Box sx={{
                            position: 'absolute',
                            left: 'calc(56px + 8px)',
                            top: 'calc(50% - 2.5px)',
                            width: 5,
                            height: 5,
                            borderRadius: '50%',
                            bgcolor: C.accent,
                          }}
                          />
                        )}
                        <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: C.txt, fontVariantNumeric: 'tabular-nums' }}>
                          {row.label}
                        </Typography>
                      </Box>
                      <Box sx={{ ...LANE_CELL_SX, gridColumn: '4' }}>
                        <Box sx={{
                          ...LANE_INNER_SX,
                          ...laneFill(row.alarms, view.laneMax.alarms, RAMP_ALARMS, row.lowSample),
                        }}
                        />
                      </Box>
                      <Box sx={{ ...LANE_CELL_SX, gridColumn: '5' }}>
                        <Box sx={{
                          ...LANE_INNER_SX,
                          ...laneFill(secondary, view.laneMax[lane.field], RAMP_LANE, row.lowSample),
                        }}
                        />
                      </Box>
                      <Box sx={{ gridColumn: '6', display: 'flex', justifyContent: 'flex-start', pl: 1.3, minWidth: 0 }}>
                        <Box sx={{
                          height: 10,
                          borderRadius: '2px',
                          minWidth: '2px',
                          width: `${((row.fedPct || 0) / maxPercent) * 100}%`,
                          background: row.lowSample
                            ? 'repeating-linear-gradient(135deg,rgba(67,196,122,.8) 0 3px,rgba(67,196,122,.22) 3px 7px)'
                            : C.fed,
                        }}
                        />
                      </Box>
                      <Typography sx={{
                        gridColumn: '7',
                        textAlign: 'right',
                        fontSize: 12,
                        fontWeight: 700,
                        color: C.txt,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                      >
                        {row.fedPct !== null && row.fedPct >= 0.5 ? `${decimals(row.fedPct, 0)}%` : '·'}
                      </Typography>
                    </Box>
                  </Tooltip>
                );
              })}

              {/* Leyenda: sin el máximo impreso, el color de un carril no significa nada. */}
              <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 2,
                mt: 1.4,
                pt: 1.2,
                borderTop: `1px solid ${C.border}`,
              }}
              >
                {[
                  { label: translate('analytics_depth_alarm_count'), ramp: RAMP_ALARMS, max: counter(view.laneMax.alarms), colour: C.err },
                  { label: laneLabel, ramp: RAMP_LANE, max: laneAvailable ? decimals(view.laneMax[lane.field], lane.dec) : '—', colour: C.txt2 },
                ].map((unit) => (
                  <Box key={unit.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                    <Typography sx={{ ...GROUP_SX, color: unit.colour }}>{unit.label}</Typography>
                    <Box sx={{ display: 'flex', width: 88 }}>
                      {unit.ramp.map((step) => (
                        <Box key={step} sx={{ flex: 1, height: 7, bgcolor: step }} />
                      ))}
                    </Box>
                    <Typography sx={{ fontSize: 10.5, color: C.txt3, fontVariantNumeric: 'tabular-nums' }}>
                      {translate('analytics_depth_ramp_max', { max: unit.max })}
                    </Typography>
                  </Box>
                ))}
                {[
                  { key: 'analytics_depth_legend_zero', style: { bgcolor: ZERO_FILL, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.06)' } },
                  { key: 'analytics_depth_legend_low', style: { background: lowFill('rgba(255,82,82,.8)') } },
                  { key: 'analytics_depth_legend_no_data', style: { background: NO_DATA_FILL, boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.05)' } },
                ].map((key) => (
                  <Box key={key.key} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
                    <Box sx={{ width: 12, height: 12, borderRadius: '2px', ...key.style }} />
                    <Typography sx={{ fontSize: 10.5, color: C.txt3 }}>{translate(key.key)}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* ---------- tira inferior ---------- */}
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: 3.5,
              mt: 2,
              pt: 1.6,
              borderTop: `1px solid ${C.border}`,
            }}
            >
              <Block title={translate('analytics_depth_time')}>
                <Row label={translate('analytics_depth_fed')} value={formatDuration(view.summary.fedMinutes)} />
                <Row label={translate('analytics_depth_unfed')} value={formatDuration(view.summary.unfedMinutes)} />
                <Row
                  label={translate('analytics_depth_no_data')}
                  value={formatDuration(view.summary.noData)}
                  valueColor={C.warn}
                  muted
                />
                <Row label={translate('analytics_depth_transit')} value={formatDuration(view.summary.transit)} muted />
              </Block>

              <Block title={translate('analytics_depth_winch')}>
                <Row label={translate('analytics_depth_repositions')} value={counter(view.summary.repositions)} />
                <Box sx={{
                  height: 7,
                  borderRadius: '3px',
                  overflow: 'hidden',
                  display: 'flex',
                  my: 0.8,
                  bgcolor: 'rgba(255,255,255,.06)',
                }}
                >
                  <Box sx={{
                    bgcolor: C.accent,
                    width: view.summary.repositions
                      ? `${(100 * view.summary.operatorRepositions) / view.summary.repositions}%`
                      : 0,
                  }}
                  />
                  <Box sx={{
                    bgcolor: C.unfed,
                    opacity: 0.5,
                    width: view.summary.repositions
                      ? `${(100 * view.summary.routineRepositions) / view.summary.repositions}%`
                      : 0,
                  }}
                  />
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography sx={{ fontSize: 10.5, color: C.accent }}>
                    {`${translate('analytics_depth_operator')} ${counter(view.summary.operatorRepositions)}`}
                  </Typography>
                  <Typography sx={{ fontSize: 10.5, color: C.txt3 }}>
                    {`${translate('analytics_depth_routine')} ${counter(view.summary.routineRepositions)}`}
                  </Typography>
                </Box>
                <Box sx={{ mt: 0.7 }}>
                  <Row
                    label={translate('analytics_depth_range')}
                    value={view.summary.minM === null ? '—' : `${metres(view.summary.minM)} – ${metres(view.summary.maxM)} m`}
                    muted
                  />
                  {(isCenter || view.routes.length > 1) && (
                    <Row label={translate('analytics_depth_equipment')} value={counter(view.routes.length)} muted />
                  )}
                  {view.duplicatesOmitted > 0 && (
                    <Typography sx={{ fontSize: 10, color: C.txt3, mt: 0.4 }}>
                      {translate('analytics_depth_shared_equipment', { count: view.duplicatesOmitted })}
                    </Typography>
                  )}
                </Box>
              </Block>

              <Block title={translate('analytics_depth_alarm_count')}>
                <Row label={translate('analytics_depth_with_depth')} value={counter(view.visibleAlarms)} />
                <Row label={translate('analytics_depth_unknown_alarms')} value={counter(view.unknownAlarms)} muted />
                <Row
                  label={translate('analytics_depth_below_threshold')}
                  value={counter(view.hiddenAlarms)}
                  valueColor={C.warn}
                  muted
                />
              </Block>
            </Box>

            {/* ---------- detalle ---------- */}
            <Box sx={{ borderTop: `1px solid ${C.border}`, mt: 2 }}>
              <Button
                size="small"
                onClick={() => setTableOpen((previous) => !previous)}
                sx={{ ...SECTION_SX, textTransform: 'none', px: 0, py: 1.1, gap: 0.9 }}
              >
                <Box component="span" sx={{ color: C.txt3, fontSize: 13, width: 9 }}>
                  {tableOpen ? '⌄' : '›'}
                </Box>
                {translate('analytics_depth_data')}
                <Box component="span" sx={{ fontSize: 11, fontWeight: 400, color: C.txt4 }}>
                  {translate('analytics_depth_bands_with_data', { count: view.rows.length })}
                </Box>
              </Button>
              <Collapse in={tableOpen}>
                <TableContainer sx={{ maxHeight: '38vh' }}>
                  <Table size="small" stickyHeader aria-label={translate('analytics_depth_title')}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={headCellSx}>{translate('analytics_depth_bin')}</TableCell>
                        <TableCell sx={headCellSx} align="right">{translate('analytics_depth_fed')}</TableCell>
                        <TableCell sx={headCellSx} align="right">{translate('analytics_depth_unfed')}</TableCell>
                        <TableCell sx={headCellSx} align="right">{translate('analytics_depth_alarm_count')}</TableCell>
                        <TableCell sx={headCellSx} align="right">{translate('analytics_depth_perdidos')}</TableCell>
                        <TableCell sx={headCellSx} align="right">{translate('analytics_depth_food_kg')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {view.rows.map((row) => (
                        <TableRow key={row.label}>
                          <TableCell sx={bodyCellSx}>{`${row.label} m`}</TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {row.fed === null ? '—' : `${formatDuration(row.fed)} · ${decimals(row.fedPct)}%`}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {row.unfed === null ? '—' : `${formatDuration(row.unfed)} · ${decimals(row.unfedPct)}%`}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">{counter(row.alarms)}</TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {row.perdidos === null ? '—' : counter(row.perdidos)}
                          </TableCell>
                          <TableCell sx={bodyCellSx} align="right">
                            {row.alimentoKg === null ? '—' : decimals(row.alimentoKg)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Collapse>
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: `1px solid ${C.border}`, py: 1, px: 2.5 }}>
        <Button
          onClick={onClose}
          sx={{
            fontSize: 12,
            textTransform: 'none',
            color: C.accent,
            border: `1px solid ${C.accent}`,
            bgcolor: C.accentSoft,
          }}
        >
          {translate('analytics_close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DepthAnalyticsDialog;
